import { createFileRoute } from '@tanstack/react-router'
import { useMemo } from 'react'

import type { Device } from '@/api/admin-devices-types'
import type {
	ForcedFormFactorType,
	ForcedOrientationType,
	LogStreamLevelType,
	OperatingModeType,
	PosMutationPermissionType
} from '@/api/admin-sales-channels-types'
import {
	ForcedFormFactor,
	ForcedOrientation,
	OperatingMode,
	PosMutationPermission
} from '@/api/admin-sales-channels-types'
import { useUpdateDevice } from '@/api/api-mutations'
import { useListMarketingChannels } from '@/api/react-query'
import { Button } from '@/components/base/button'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { toast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup, FormHint, FormLabel, useAppForm } from '@/components/form/form'
import { RouteModal, RouteModalFormBlocker, useRouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'

const OPERATING_MODE_VALUES = Object.values(OperatingMode) as OperatingModeType[]
const LOG_STREAM_LEVEL_VALUES: LogStreamLevelType[] = ['OFF', 'ERROR', 'WARN', 'INFO', 'DEBUG']
const ORIENTATION_VALUES = Object.values(ForcedOrientation) as ForcedOrientationType[]
const FORM_FACTOR_VALUES = Object.values(ForcedFormFactor) as ForcedFormFactorType[]
const PERMISSION_VALUES = Object.values(PosMutationPermission) as PosMutationPermissionType[]

// CloudWatch Logs stream names may not exceed 512 chars or contain ':'/'*'; the server prefixes
// this value with `${tenantId}/`, so keep some headroom rather than validating the exact API limit.
const MAX_LOG_STREAM_ID_LENGTH = 400

const PERMISSION_FIELDS = ['browseEnabled', 'adjustQuantity', 'clearCart', 'itemDeletion', 'orderRestart'] as const
const SOUND_FIELDS = [
	'itemAddedSound',
	'productNotFoundSound',
	'addToCartFailedSound',
	'paymentSuccessSound',
	'paymentFailedSound'
] as const
const SECONDS_FIELDS = ['inactivityThresholdSeconds', 'abandonCountdownSeconds', 'coolerReadyTimeoutSeconds'] as const
const INSET_FIELDS = ['safeAreaInsetTop', 'safeAreaInsetRight', 'safeAreaInsetBottom', 'safeAreaInsetLeft'] as const

const isJsonObject = (value: string) => {
	try {
		const parsed = JSON.parse(value)
		return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
	} catch {
		return false
	}
}

/** Empty string means "unset" in the form; the API clears an override with `null`. */
const textOrNull = (value: string) => value.trim() || null
const numberOrNull = (value: number | undefined) => value ?? null

export const Route = createFileRoute('/_authenticated/settings/devices/$deviceUuid/edit')({
	component: DeviceEdit,
	staticData: {
		crumb: ({ context: { L } }) => L.General.Edit()
	}
})

function DeviceEdit() {
	const { deviceUuid } = Route.useParams()

	return (
		<RouteModal prev='..' asDrawer requiresData loader={async api => await api.getDevice({ path: { deviceUuid } })}>
			{data => <DeviceEditForm device={data} />}
		</RouteModal>
	)
}

function DeviceEditForm({ device }: { device: Device }) {
	const { L } = useI18n()
	const { deviceUuid } = Route.useParams()
	const { handleSuccess } = useRouteModal()
	const updateDevice = useUpdateDevice()
	const marketingChannels = useListMarketingChannels()

	const marketingChannelOptions = useMemo(
		() => [
			{ value: '', label: L.Settings.Devices.NoMarketingChannel() },
			...(marketingChannels.data ?? []).map(mc => ({ value: mc.uuid, label: mc.name }))
		],
		[L, marketingChannels.data]
	)

	const operatingModeOptions = useMemo(
		() => OPERATING_MODE_VALUES.map(value => ({ label: L.Settings.PointOfSale.OperatingMode(value), value })),
		[L]
	)

	const logStreamLevelOptions = useMemo(
		() => LOG_STREAM_LEVEL_VALUES.map(value => ({ label: L.Settings.PointOfSale.LogStreamLevel(value), value })),
		[L]
	)
	const orientationOptions = useMemo(
		() => [
			{ value: '', label: L.Settings.Devices.Config.Default() },
			...ORIENTATION_VALUES.map(value => ({ value, label: value }))
		],
		[L]
	)
	const formFactorOptions = useMemo(
		() => [
			{ value: '', label: L.Settings.Devices.Config.Default() },
			...FORM_FACTOR_VALUES.map(value => ({ value, label: value }))
		],
		[L]
	)
	const permissionOptions = useMemo(
		() => [
			{ value: '', label: L.Settings.Devices.Config.Default() },
			...PERMISSION_VALUES.map(value => ({ value, label: L.Settings.Devices.Config.Permission(value) }))
		],
		[L]
	)
	const printerWarningOptions = useMemo(
		() => [
			{ value: '', label: L.Settings.Devices.Config.Default() },
			{ value: 'true', label: L.Settings.Devices.Config.Enabled() },
			{ value: 'false', label: L.Settings.Devices.Config.Disabled() }
		],
		[L]
	)

	const config = device.config
	const permissionLabels = {
		browseEnabled: L.Settings.Devices.Config.BrowseEnabled(),
		adjustQuantity: L.Settings.Devices.Config.AdjustQuantity(),
		clearCart: L.Settings.Devices.Config.ClearCart(),
		itemDeletion: L.Settings.Devices.Config.ItemDeletion(),
		orderRestart: L.Settings.Devices.Config.OrderRestart()
	}
	const soundLabels = {
		itemAddedSound: L.Settings.Devices.Config.ItemAddedSound(),
		productNotFoundSound: L.Settings.Devices.Config.ProductNotFoundSound(),
		addToCartFailedSound: L.Settings.Devices.Config.AddToCartFailedSound(),
		paymentSuccessSound: L.Settings.Devices.Config.PaymentSuccessSound(),
		paymentFailedSound: L.Settings.Devices.Config.PaymentFailedSound()
	}
	const secondsLabels = {
		inactivityThresholdSeconds: L.Settings.Devices.Config.InactivityThresholdSeconds(),
		abandonCountdownSeconds: L.Settings.Devices.Config.AbandonCountdownSeconds(),
		coolerReadyTimeoutSeconds: L.Settings.Devices.Config.CoolerReadyTimeoutSeconds()
	}
	const insetLabels = {
		safeAreaInsetTop: L.Settings.PointOfSale.SafeAreaInsetTop(),
		safeAreaInsetRight: L.Settings.PointOfSale.SafeAreaInsetRight(),
		safeAreaInsetBottom: L.Settings.PointOfSale.SafeAreaInsetBottom(),
		safeAreaInsetLeft: L.Settings.PointOfSale.SafeAreaInsetLeft()
	}

	const form = useAppForm({
		defaultValues: {
			name: device.name,
			identifier: device.identifier,
			allowedOperatingModes: device.allowedOperatingModes,
			marketingChannelUuid: device.marketingChannel?.uuid ?? '',
			defaultPaymentMethod: config.defaultPaymentMethod ?? '',
			forcedOrientation: (config.forcedOrientation ?? '') as ForcedOrientationType | '',
			forcedFormFactor: (config.forcedFormFactor ?? '') as ForcedFormFactorType | '',
			language: config.language ?? '',
			showPrinterDisconnected:
				config.showPrinterDisconnected === null || config.showPrinterDisconnected === undefined
					? ''
					: String(config.showPrinterDisconnected),
			productListColumns: config.productListColumns ?? undefined,
			browseEnabled: (config.browseEnabled ?? '') as PosMutationPermissionType | '',
			adjustQuantity: (config.adjustQuantity ?? '') as PosMutationPermissionType | '',
			clearCart: (config.clearCart ?? '') as PosMutationPermissionType | '',
			itemDeletion: (config.itemDeletion ?? '') as PosMutationPermissionType | '',
			orderRestart: (config.orderRestart ?? '') as PosMutationPermissionType | '',
			splitPercentHorizontal: config.splitPercentHorizontal ?? undefined,
			splitPercentVertical: config.splitPercentVertical ?? undefined,
			itemAddedSound: config.itemAddedSound ?? '',
			productNotFoundSound: config.productNotFoundSound ?? '',
			addToCartFailedSound: config.addToCartFailedSound ?? '',
			paymentSuccessSound: config.paymentSuccessSound ?? '',
			paymentFailedSound: config.paymentFailedSound ?? '',
			logStreamLevel: (config.logStreamLevel ?? 'WARN') as LogStreamLevelType,
			logStreamId: config.logStreamId ?? '',
			inactivityThresholdSeconds: config.inactivityThresholdSeconds ?? undefined,
			abandonCountdownSeconds: config.abandonCountdownSeconds ?? undefined,
			coolerReadyTimeoutSeconds: config.coolerReadyTimeoutSeconds ?? undefined,
			safeAreaInsetTop: config.safeAreaInsets?.top ?? undefined,
			safeAreaInsetRight: config.safeAreaInsets?.right ?? undefined,
			safeAreaInsetBottom: config.safeAreaInsets?.bottom ?? undefined,
			safeAreaInsetLeft: config.safeAreaInsets?.left ?? undefined,
			checkoutModeIdleTimeoutSeconds: config.checkoutModeIdleTimeoutSeconds ?? undefined,
			checkoutModeTheme: config.checkoutModeTheme ? JSON.stringify(config.checkoutModeTheme, null, 2) : ''
		},
		onSubmit: async ({ value }) => {
			const safeAreaInsets = {
				top: value.safeAreaInsetTop,
				right: value.safeAreaInsetRight,
				bottom: value.safeAreaInsetBottom,
				left: value.safeAreaInsetLeft
			}
			const theme = value.checkoutModeTheme.trim()
			const result = await updateDevice({
				path: { deviceUuid },
				body: {
					name: value.name,
					identifier: value.identifier.trim(),
					allowedOperatingModes: value.allowedOperatingModes,
					// Empty selection unlinks (null); undefined would leave it unchanged.
					marketingChannelUuid: value.marketingChannelUuid || null,
					defaultPaymentMethod: textOrNull(value.defaultPaymentMethod),
					forcedOrientation: value.forcedOrientation || null,
					forcedFormFactor: value.forcedFormFactor || null,
					language: textOrNull(value.language),
					showPrinterDisconnected:
						value.showPrinterDisconnected === '' ? null : value.showPrinterDisconnected === 'true',
					productListColumns: numberOrNull(value.productListColumns),
					browseEnabled: value.browseEnabled || null,
					adjustQuantity: value.adjustQuantity || null,
					clearCart: value.clearCart || null,
					itemDeletion: value.itemDeletion || null,
					orderRestart: value.orderRestart || null,
					splitPercentHorizontal: numberOrNull(value.splitPercentHorizontal),
					splitPercentVertical: numberOrNull(value.splitPercentVertical),
					itemAddedSound: textOrNull(value.itemAddedSound),
					productNotFoundSound: textOrNull(value.productNotFoundSound),
					addToCartFailedSound: textOrNull(value.addToCartFailedSound),
					paymentSuccessSound: textOrNull(value.paymentSuccessSound),
					paymentFailedSound: textOrNull(value.paymentFailedSound),
					logStreamLevel: value.logStreamLevel,
					logStreamId: textOrNull(value.logStreamId),
					inactivityThresholdSeconds: numberOrNull(value.inactivityThresholdSeconds),
					abandonCountdownSeconds: numberOrNull(value.abandonCountdownSeconds),
					coolerReadyTimeoutSeconds: numberOrNull(value.coolerReadyTimeoutSeconds),
					// `null` when every side is blank, so clearing them all removes the override.
					safeAreaInsets: Object.values(safeAreaInsets).some(v => v !== undefined) ? safeAreaInsets : null,
					checkoutModeIdleTimeoutSeconds: numberOrNull(value.checkoutModeIdleTimeoutSeconds),
					checkoutModeTheme: theme ? JSON.parse(theme) : null
				}
			})
			if (result.isSuccess) {
				toast.success(L.Settings.Devices.UpdateSuccess())
				handleSuccess()
			} else {
				toast.error((result.response as any)?.message ?? L.Error.SomethingWentWrong())
			}
		}
	})

	return (
		<form.AppForm>
			<RouteModalFormBlocker form={form as any} />
			<Drawer.Header>
				<Drawer.Title asChild>
					<Heading>{L.Settings.Devices.EditTitle()}</Heading>
				</Drawer.Title>
			</Drawer.Header>
			<Drawer.Body className='flex-1 overflow-auto'>
				<FormContainer>
					<FormFieldGroup>
						<form.AppField
							name='name'
							validators={{ onChange: ({ value }) => (!value ? L.General.Required() : undefined) }}
							children={field => (
								<field.TextField
									autoFocus
									label={L.Fields.Name()}
									placeholder={L.Settings.Devices.NamePlaceholder()}
								/>
							)}
						/>
					</FormFieldGroup>
					<FormFieldGroup>
						<form.AppField
							name='identifier'
							validators={{
								onChange: ({ value }) => (!value.trim() ? L.General.Required() : undefined)
							}}
							children={field => <field.TextField label={L.Settings.Devices.Identifier()} />}
						/>
					</FormFieldGroup>
					<FormFieldGroup>
						<form.AppField
							name='allowedOperatingModes'
							children={field => (
								<field.ComboboxField
									label={L.Settings.Devices.OperatingModes()}
									placeholder={L.Settings.PointOfSale.SelectOperatingModes()}
									multiple
									options={operatingModeOptions}
								/>
							)}
						/>
					</FormFieldGroup>
					<FormFieldGroup>
						<form.AppField
							name='marketingChannelUuid'
							children={field => (
								<field.SelectField
									label={L.Settings.Devices.MarketingChannel()}
									options={marketingChannelOptions}
									optionsLoading={marketingChannels.isLoading}
								/>
							)}
						/>
						<FormHint>{L.Settings.Devices.MarketingChannelDescription()}</FormHint>
					</FormFieldGroup>
					<FormSection title={L.Settings.Devices.Config.GeneralSection()} />
					<FormFieldGroup>
						<form.AppField
							name='defaultPaymentMethod'
							children={field => (
								<field.TextField label={L.Settings.Devices.Config.DefaultPaymentMethod()} optional />
							)}
						/>
					</FormFieldGroup>
					<FormFieldGroup>
						<form.AppField
							name='language'
							children={field => (
								<field.TextField label={L.Settings.Devices.Config.Language()} optional />
							)}
						/>
					</FormFieldGroup>
					<FormFieldGroup>
						<form.AppField
							name='showPrinterDisconnected'
							children={field => (
								<field.SelectField
									label={L.Settings.Devices.Config.ShowPrinterDisconnected()}
									options={printerWarningOptions}
								/>
							)}
						/>
					</FormFieldGroup>

					<FormSection title={L.Settings.Devices.Config.DisplaySection()} />
					<FormFieldGroup>
						<form.AppField
							name='forcedOrientation'
							children={field => (
								<field.SelectField
									label={L.Settings.Devices.Config.ForcedOrientation()}
									options={orientationOptions}
								/>
							)}
						/>
					</FormFieldGroup>
					<FormFieldGroup>
						<form.AppField
							name='forcedFormFactor'
							children={field => (
								<field.SelectField
									label={L.Settings.Devices.Config.ForcedFormFactor()}
									options={formFactorOptions}
								/>
							)}
						/>
					</FormFieldGroup>
					<FormFieldGroup>
						<form.AppField
							name='productListColumns'
							children={field => (
								<field.NumberField
									label={L.Settings.Devices.Config.ProductListColumns()}
									min={1}
									optional
								/>
							)}
						/>
					</FormFieldGroup>
					<FormFieldGroup>
						<FormLabel optional>{L.Settings.PointOfSale.SafeAreaInsetsLabel()}</FormLabel>
						<FormHint>{L.Settings.PointOfSale.SafeAreaInsetsHint()}</FormHint>
						<div className='grid grid-cols-2 gap-4 sm:grid-cols-4'>
							{INSET_FIELDS.map(name => (
								<form.AppField
									key={name}
									name={name}
									children={field => <field.NumberField label={insetLabels[name]} min={0} />}
								/>
							))}
						</div>
					</FormFieldGroup>

					<FormSection title={L.Settings.Devices.Config.ScoSection()} />
					{PERMISSION_FIELDS.map(name => (
						<FormFieldGroup key={name}>
							<form.AppField
								name={name}
								children={field => (
									<field.SelectField label={permissionLabels[name]} options={permissionOptions} />
								)}
							/>
						</FormFieldGroup>
					))}
					<FormFieldGroup>
						<form.AppField
							name='splitPercentHorizontal'
							children={field => (
								<field.NumberField
									label={L.Settings.Devices.Config.SplitPercentHorizontal()}
									min={0}
									max={100}
									optional
								/>
							)}
						/>
					</FormFieldGroup>
					<FormFieldGroup>
						<form.AppField
							name='splitPercentVertical'
							children={field => (
								<field.NumberField
									label={L.Settings.Devices.Config.SplitPercentVertical()}
									min={0}
									max={100}
									optional
								/>
							)}
						/>
					</FormFieldGroup>
					{SECONDS_FIELDS.map(name => (
						<FormFieldGroup key={name}>
							<form.AppField
								name={name}
								children={field => <field.NumberField label={secondsLabels[name]} min={0} optional />}
							/>
						</FormFieldGroup>
					))}

					<FormSection title={L.Settings.Devices.Config.SoundsSection()} />
					{SOUND_FIELDS.map(name => (
						<FormFieldGroup key={name}>
							<form.AppField
								name={name}
								children={field => <field.TextField label={soundLabels[name]} optional />}
							/>
						</FormFieldGroup>
					))}

					<FormSection title={L.Settings.Devices.Config.CheckoutSection()} />
					<FormFieldGroup>
						<form.AppField
							name='checkoutModeIdleTimeoutSeconds'
							children={field => (
								<field.NumberField
									label={L.Settings.Devices.Config.CheckoutModeIdleTimeoutSeconds()}
									min={0}
									optional
								/>
							)}
						/>
					</FormFieldGroup>
					<FormFieldGroup>
						<form.AppField
							name='checkoutModeTheme'
							validators={{
								onChange: ({ value }) =>
									value.trim() && !isJsonObject(value)
										? L.Settings.Devices.Config.InvalidJson()
										: undefined
							}}
							children={field => (
								<field.TextAreaField label={L.Settings.Devices.Config.CheckoutModeTheme()} optional />
							)}
						/>
						<FormHint>{L.Settings.Devices.Config.CheckoutModeThemeHint()}</FormHint>
					</FormFieldGroup>

					<FormSection title={L.Settings.Devices.Config.LoggingSection()} />
					<FormFieldGroup>
						<form.AppField
							name='logStreamLevel'
							children={field => (
								<field.SelectField
									label={L.Settings.PointOfSale.LogStreamLevelLabel()}
									options={logStreamLevelOptions}
								/>
							)}
						/>
					</FormFieldGroup>
					<FormFieldGroup>
						<form.AppField
							name='logStreamId'
							validators={{
								onChange: ({ value }) => {
									if (!value) {
										return undefined
									}
									if (/[:*]/.test(value)) {
										return L.Settings.PointOfSale.LogStreamIdInvalidChars()
									}
									if (value.length > MAX_LOG_STREAM_ID_LENGTH) {
										return L.Settings.PointOfSale.LogStreamIdTooLong()
									}
									return undefined
								}
							}}
							children={field => (
								<field.TextField
									label={L.Settings.PointOfSale.LogStreamIdLabel()}
									optional
									placeholder={L.Settings.PointOfSale.LogStreamIdPlaceholder()}
								/>
							)}
						/>
					</FormFieldGroup>
				</FormContainer>
			</Drawer.Body>
			<Drawer.Footer>
				<div className='flex items-center justify-end gap-x-2'>
					<Drawer.Close asChild>
						<Button variant='secondary' size='small'>
							{L.General.Cancel()}
						</Button>
					</Drawer.Close>
					<form.SaveButton />
				</div>
			</Drawer.Footer>
		</form.AppForm>
	)
}

function FormSection({ title }: { title: string }) {
	return (
		<Heading level='h3' className='border-b border-ui-border-base pb-2 pt-2'>
			{title}
		</Heading>
	)
}
